/** @jsxImportSource @emotion/react */
import * as yup from "yup";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { Product } from "../models/product";
import { useListPackingItems } from "../../packing-items/service/packing-items-api";
import { useSelector } from "react-redux";
import * as authSelectors from "../../auth/authSelectors";
import { Drawer } from "../../../components/Drawer";
import { ButtonAction } from "../../../components/NewButtons";
import { useUpdateProduct } from "../service/products-api";
import { StyledForm } from "../../../components/StyledForm";
import { SelectField } from "../../../components/forms/fields/SelectField";
import { css } from "@emotion/react";
import { ActionSpinner } from "../../../components/ActionSpinner";
import { yupResolver } from "@hookform/resolvers/yup";
import { toastr } from "react-redux-toastr";
import { getErrorMessage, useHandleFetchError } from "../../../error-handler";
import { CardInformationRow } from "../../../components/page-skeleton/CardInformationRow";
import { TextInput } from "../../../components/forms/inputs/TextInput";
import { Divider } from "../../../components/Divider";
import { SpinnerBlock } from "../../common/Spinner";

type Props = {
  product: Product;
  isOpen: boolean;
  handleOnClose: () => void;
};

const selectorWrapperStyle = css`
  display: flex;
  padding: 16px;
  width: 100%;
`;

const additionalFormStyles = css`
  gap: 16px;
  align-items: flex-start;
`;

const fieldsWrapperStyle = css`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

type FormValues = {
  packingItems: { amount: number | string; id: string }[];
};

const validationSchema = yup.object({
  packingItems: yup
    .array()
    .of(
      yup.object().shape({
        amount: yup
          .number()
          .typeError("Enter amount to continue")
          .required("Amount is required"),
        id: yup.string().required("Packing item is required"),
      })
    )
    .min(1, "Choose at least one packing item to add"),
});

export const AddPackingItemsDrawer = ({
  product,
  isOpen,
  handleOnClose,
}: Props) => {
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const formMethods = useForm<FormValues>({
    defaultValues: {
      packingItems: [],
    },
    resolver: yupResolver(validationSchema),
  });

  const {
    data: packingItems,
    isLoading: isLoadingPackingItems,
    error: listPackingItemsError,
  } = useListPackingItems(organizationKeyName);

  const { mutateAsync: updateProduct, isLoading } = useUpdateProduct(
    product.id
  );

  const { fields, replace } = useFieldArray({
    control: formMethods.control,
    name: "packingItems",
  });

  const onSubmit = async (data: FormValues) => {
    try {
      await updateProduct({
        organizationKeyName,
        data: {
          ...product,
          labels: (product.labels || []).map((l) => l.id),
          tags: (product.tags || []).map((tag) => tag.id),
          additionGroups: (product.additionGroups || []).map(
            (group) => group.id
          ),
          productPackagingInstructions: [
            ...(product.packagingInstructions || []).map((pi) => ({
              id: pi.id,
              amount: pi.amount,
            })),
            ...data.packingItems,
          ],
        },
      });
      formMethods.reset();
      handleOnClose();
      toastr.success("Success", "Packing items successfully added");
    } catch (error: unknown) {
      const message = getErrorMessage(error);
      toastr.error("Error", message);
    }
  };

  const packingItemOptions = (packingItems || [])
    .map((ing) => ({
      value: ing.id,
      label: ing.name,
    }))
    .filter((ing) => {
      return !product.packagingInstructions?.find((pi) => pi.id === ing.value);
    });

  useHandleFetchError(listPackingItemsError);

  return (
    <Drawer
      title="Add product packing item"
      width={700}
      buttons={
        <ButtonAction
          onClick={formMethods.handleSubmit(onSubmit)}
          disabled={isLoading}
          type="button"
        >
          {isLoading ? <ActionSpinner /> : "Save"}
        </ButtonAction>
      }
      open={isOpen}
      styles={{
        body: {
          padding: "0px",
        },
      }}
      onClose={() => {
        handleOnClose();
        formMethods.reset();
      }}
    >
      <FormProvider {...formMethods}>
        <StyledForm additionalStyles={additionalFormStyles}>
          {isLoadingPackingItems ? (
            <SpinnerBlock />
          ) : (
            <>
              <div css={selectorWrapperStyle}>
                <SelectField
                  isMulti
                  label="Packing items"
                  closeMenuOnSelect={false}
                  helperText="Choose packing items you want to add, and enter the amount for each packing item."
                  placeholder="Start typing to search for packing items..."
                  errorMessage={
                    formMethods.getFieldState(
                      "packingItems",
                      formMethods.formState
                    ).error?.message
                  }
                  onChange={(value) => {
                    const existingPackingItems = formMethods.watch(
                      "packingItems"
                    );
                    const newPackingItems = (value as {
                      value: string;
                      label: string;
                    }[]).map((x) => ({
                      id: x.value,
                      amount:
                        existingPackingItems.find((ei) => ei.id === x.value)
                          ?.amount || "",
                    }));
                    replace(newPackingItems);
                  }}
                  options={packingItemOptions}
                  value={packingItemOptions?.filter((option) => {
                    return formMethods
                      .watch("packingItems")
                      .find((ing) => ing.id === option.value);
                  })}
                />
              </div>
              <div css={fieldsWrapperStyle}>
                <Divider />
                {fields.map((_, i) => {
                  const formPackingItem = formMethods.watch(
                    `packingItems.${i}`
                  );
                  return (
                    <CardInformationRow
                      titleVerticallyCentered
                      title={
                        packingItems?.find((pi) => pi.id === formPackingItem.id)
                          ?.name || formPackingItem.id
                      }
                    >
                      <TextInput
                        name={`packingItems.${i}.amount`}
                        label="Amount"
                        type="number"
                        placeholder="Enter amount for this packing item"
                      />
                    </CardInformationRow>
                  );
                })}
              </div>
            </>
          )}
        </StyledForm>
      </FormProvider>
    </Drawer>
  );
};
